import { useCallback, useMemo } from 'react';

import { MultiSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { useFormContext } from '@/components/react-hook-form';
import { useSuggestionAcknowledgementContext } from '@/modules/aiSuggestions/context/SuggestionAcknowledgement.context';
import { useTestamentaryEntitySuggestionsContextIfAvailable } from '@/modules/aiSuggestions/context/TestamentaryEntitySuggestions.context';
import { CommonAiSuggestionFragment } from '@/modules/aiSuggestions/graphql/aiSuggestions.generated';
import { AiSuggestionAcceptanceStatus, AiSuggestionKind } from '@/types/schema';

import { TESTAMENTARY_ENTITY } from '../EditTestamentaryEntitySplitScreen.constants';
import {
  EditTestamentaryEntityFormField,
  EditTestamentaryEntityFormShape,
} from '../EditTestamentaryEntitySplitScreen.types';
import { findSuggestedCreatedFromOption } from './useSubtrustCreatedFromSuggestion.utils';

export const createdFromFieldName =
  `${TESTAMENTARY_ENTITY}.createdFromEntityIds` as const satisfies EditTestamentaryEntityFormField;

interface UseSubtrustCreatedFromSuggestionResult {
  createdFromSuggestion: CommonAiSuggestionFragment | undefined;
  createdFromSuggestionAcknowledged: boolean;
  createdFromSuggestionDisplay: string;
  canAcceptCreatedFromSuggestion: boolean;
  /**
   * Human-readable reason explaining why the suggestion can't be accepted yet.
   * `undefined` when the suggestion is acceptable.
   */
  createdFromSuggestionDisableReason: string | undefined;
  handleAcceptCreatedFromSuggestion: () => void;
  handleRejectCreatedFromSuggestion: () => void;
}

export function useSubtrustCreatedFromSuggestion(
  trustEntityOptions: MultiSelectInputOption[]
): UseSubtrustCreatedFromSuggestionResult {
  const { getValues, setShouldBlockNavigation, setValue } =
    useFormContext<EditTestamentaryEntityFormShape>();
  const suggestionsContext =
    useTestamentaryEntitySuggestionsContextIfAvailable();
  const { acknowledgedSuggestions, acknowledgeSuggestion } =
    useSuggestionAcknowledgementContext();

  const createdFromSuggestion =
    suggestionsContext?.suggestionsByKind[
      AiSuggestionKind.SubtrustCreatedFrom
    ]?.[0];
  const createdFromSuggestionAcknowledged = createdFromSuggestion
    ? Boolean(acknowledgedSuggestions[createdFromSuggestion.id])
    : true;

  const suggestedCreatedFromOption = useMemo(() => {
    return findSuggestedCreatedFromOption(
      createdFromSuggestion,
      trustEntityOptions
    );
  }, [createdFromSuggestion, trustEntityOptions]);

  const createdFromSuggestionDisplay = useMemo(() => {
    if (suggestedCreatedFromOption) {
      return suggestedCreatedFromOption.display;
    }

    return createdFromSuggestion?.subtrustCreatedFrom?.displayName ?? '';
  }, [createdFromSuggestion, suggestedCreatedFromOption]);

  const canAcceptCreatedFromSuggestion = Boolean(suggestedCreatedFromOption);

  // When the parent trust hasn't been created yet (no resolved entity), we
  // can't apply the suggestion. Direct the user to accept the prerequisite
  // suggestion on the Estate tab first.
  const createdFromSuggestionDisableReason = !canAcceptCreatedFromSuggestion
    ? 'Please create this trust from the Estate tab suggestion before accepting this suggestion.'
    : undefined;

  const handleAcceptCreatedFromSuggestion = useCallback(() => {
    if (!createdFromSuggestion || !suggestedCreatedFromOption) {
      return;
    }

    const currentCreatedFromEntityIDs = getValues(createdFromFieldName) ?? [];
    const shouldAppendSuggestion = !currentCreatedFromEntityIDs.includes(
      suggestedCreatedFromOption.value
    );

    if (shouldAppendSuggestion) {
      setValue(
        createdFromFieldName,
        [...currentCreatedFromEntityIDs, suggestedCreatedFromOption.value],
        { shouldDirty: true }
      );
    }

    setShouldBlockNavigation(true);
    acknowledgeSuggestion({
      suggestionID: createdFromSuggestion.id,
      status: AiSuggestionAcceptanceStatus.Accepted,
    });
  }, [
    acknowledgeSuggestion,
    createdFromSuggestion,
    getValues,
    setShouldBlockNavigation,
    setValue,
    suggestedCreatedFromOption,
  ]);

  const handleRejectCreatedFromSuggestion = useCallback(() => {
    if (!createdFromSuggestion) {
      return;
    }

    setShouldBlockNavigation(true);
    acknowledgeSuggestion({
      suggestionID: createdFromSuggestion.id,
      status: AiSuggestionAcceptanceStatus.Rejected,
    });
  }, [acknowledgeSuggestion, createdFromSuggestion, setShouldBlockNavigation]);

  return {
    createdFromSuggestion,
    createdFromSuggestionAcknowledged,
    createdFromSuggestionDisplay,
    canAcceptCreatedFromSuggestion,
    createdFromSuggestionDisableReason,
    handleAcceptCreatedFromSuggestion,
    handleRejectCreatedFromSuggestion,
  };
}
